import { faPencil, faSitemap } from "@fortawesome/pro-solid-svg-icons";
import Badge from "@intility/bifrost-react/Badge";
import Box from "@intility/bifrost-react/Box";
import Button from "@intility/bifrost-react/Button";
import Grid from "@intility/bifrost-react/Grid";
import Icon from "@intility/bifrost-react/Icon";
import Inline from "@intility/bifrost-react/Inline";
import Message from "@intility/bifrost-react/Message";
import Tabs from "@intility/bifrost-react/Tabs";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useBlueprintAccess } from "~/api/blueprintAccess";
import { usePlatformBlueprint } from "~/api/catalog";
import type { PlatformName } from "~/api/fga-types";
import type { CatalogBlueprint, CatalogBlueprintNode } from "~/api/types";
import { ManagementAccessSection } from "../../components/management/ManagementAccessSection";
import BlueprintCanvas from "./BlueprintCanvas";
import BlueprintEditor from "./BlueprintEditor";
import { BlueprintManifestTab } from "./BlueprintManifestTab";
import BlueprintReview from "./BlueprintReview";
import SeedPanel from "./SeedPanel";

function countNodes(nodes: CatalogBlueprintNode[]): number {
  let count = 0;
  for (const node of nodes) {
    count += 1;
    count += countNodes(node.children);
  }
  return count;
}

type BlueprintTab = "overview" | "list" | "seed" | "manifest" | "access";

// The blueprint of one platform: canvas, list view, availability, manifest
// and access. Rendered in the Blueprint tab of the platform page. The
// blueprint is read by the Platform slug and the display name comes from the
// Platform (ADR-0059 decision 4).
export function BlueprintDetail({
  slug,
  displayName,
}: {
  slug: string;
  displayName: string;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState<BlueprintTab>("overview");
  // The canvas and the list view show this name for the platform node.
  const platforms = useMemo<PlatformName[]>(
    () => [{ slug, displayName }],
    [slug, displayName],
  );

  const { canView, canEdit, loaded: accessLoaded } = useBlueprintAccess(slug);

  const {
    data: bpPlatform,
    isLoading: blueprintLoading,
    error,
  } = usePlatformBlueprint(slug, accessLoaded && canView);

  if (error) {
    return (
      <Message state="alert" header="Error">
        {error instanceof Error ? error.message : String(error)}
      </Message>
    );
  }

  if (blueprintLoading || !accessLoaded) {
    return (
      <Grid gap={16} style={{ minHeight: 100, placeContent: "center" }}>
        <Icon.Spinner size={24} />
      </Grid>
    );
  }

  // The blueprint read is 404 only when fga-management-api has no Platform
  // edge for the slug (ADR-0059 decision 1); an empty blueprint is 200.
  if (canView && bpPlatform === null) {
    return (
      <Message state="alert" header="Not found">
        No blueprint exists for <code>{slug}</code>.
      </Message>
    );
  }

  // A blueprint is keyed on the bare Platform slug (ADR-0056).
  const singlePlatform = slug;
  // The canvas and the list view take the multi-platform shape.
  const blueprint: CatalogBlueprint = {
    platforms: bpPlatform ? [bpPlatform] : [],
  };
  const nodeCount = bpPlatform ? countNodes(bpPlatform.tree) : 0;
  const hasTree = (bpPlatform?.tree.length ?? 0) > 0;
  // An empty blueprint is valid: access is then limited to the platform itself, so
  // the List view tab stays open with no placed resources. The Availability
  // tab needs only the blueprint maintainer gate its endpoints enforce. If a
  // tab disappears while it is open, fall back to Overview.
  const activeTab: BlueprintTab =
    tab === "overview" ||
    tab === "list" ||
    tab === "manifest" ||
    tab === "access" ||
    (tab === "seed" && canEdit)
      ? tab
      : "overview";

  return (
    <Grid gap={24}>
      <Inline align="center" gap={8}>
        <Inline.Stretch>
          <Inline align="center" gap={8}>
            <h2 className="bf-h2" style={{ margin: 0 }}>
              Blueprint
            </h2>
            {canView &&
              (nodeCount > 0 ? (
                <Badge state="chill">Configured</Badge>
              ) : (
                <Badge state="neutral">Empty</Badge>
              ))}
            <code>{slug}</code>
          </Inline>
        </Inline.Stretch>
        {canEdit && (
          <Button
            variant={editing ? "filled" : "basic"}
            onClick={() => {
              if (editing) {
                queryClient.invalidateQueries({ queryKey: ["catalog"] });
              }
              setEditing((v) => !v);
            }}
          >
            {editing ? (
              "Done editing"
            ) : (
              <>
                <Icon icon={faPencil} marginRight />
                Edit blueprint
              </>
            )}
          </Button>
        )}
      </Inline>

      {!canView ? (
        <Box background padding={24} radius>
          <Grid gap={12}>
            <Inline align="center" gap={8}>
              <Icon icon={faSitemap} fixedWidth />
              <h3 className="bf-h3" style={{ margin: 0 }}>
                No access
              </h3>
            </Inline>
            <p
              className="bf-p"
              style={{ color: "var(--bfc-base-c-2)", margin: 0 }}
            >
              You don't have access to view this blueprint. Ask an owner to
              grant you access.
            </p>
          </Grid>
        </Box>
      ) : editing ? (
        <BlueprintEditor
          platforms={platforms}
          activePlatform={singlePlatform}
        />
      ) : (
        // A plain wrapper, not part of the Grid gap: Tabs renders a fragment.
        <div>
          <Tabs contentBackground="base-2">
            <Tabs.Item
              noPadding={hasTree}
              active={activeTab === "overview"}
              onClick={() => setTab("overview")}
              content={
                hasTree ? (
                  // The canvas fills the viewport below the top nav, the
                  // platform header, the platform tab bar, the blueprint
                  // heading and the blueprint tab bar. Nav height and page
                  // padding vary by breakpoint, so they come from CSS
                  // variables. The fixed part covers breadcrumbs, title,
                  // button row, both tab bars and the blueprint heading.
                  <div
                    style={{
                      height:
                        "calc(100dvh - var(--bf-nav-top-height, 56px) - 320px - (var(--bf-page-padding, 24px) * 4))",
                      minHeight: 480,
                    }}
                  >
                    <BlueprintCanvas
                      blueprint={blueprint}
                      platforms={platforms}
                      selectedId={null}
                      onSelect={() => {}}
                      singlePlatform={singlePlatform}
                    />
                  </div>
                ) : (
                  <Box background padding={24} radius>
                    <Grid gap={12}>
                      <Inline align="center" gap={8}>
                        <Icon icon={faSitemap} fixedWidth />
                        <h3 className="bf-h3" style={{ margin: 0 }}>
                          No resources yet
                        </h3>
                      </Inline>
                      <p
                        className="bf-p"
                        style={{ color: "var(--bfc-base-c-2)", margin: 0 }}
                      >
                        This blueprint has no placed resources. That is a valid
                        choice: access is limited to the platform itself, with
                        no resource granularity. Click{" "}
                        <strong>Edit blueprint</strong> to add resources.
                      </p>
                    </Grid>
                  </Box>
                )
              }
            >
              Overview
            </Tabs.Item>
            <Tabs.Item
              active={activeTab === "list"}
              onClick={() => setTab("list")}
              content={
                <BlueprintReview
                  blueprint={blueprint}
                  platforms={platforms}
                  singlePlatform={singlePlatform}
                  hidePlatformTitle
                  fullWidth
                />
              }
            >
              List view
            </Tabs.Item>
            {canEdit && (
              <Tabs.Item
                active={activeTab === "seed"}
                onClick={() => setTab("seed")}
                content={<SeedPanel platformSlug={slug} />}
              >
                Availability
              </Tabs.Item>
            )}
            <Tabs.Item
              active={activeTab === "manifest"}
              onClick={() => setTab("manifest")}
              content={
                <BlueprintManifestTab platformSlug={slug} canEdit={canEdit} />
              }
            >
              Manifest
            </Tabs.Item>
            <Tabs.Item
              active={activeTab === "access"}
              onClick={() => setTab("access")}
              content={
                <ManagementAccessSection
                  objectId={`blueprint:${slug}`}
                  objectLabel="blueprint"
                />
              }
            >
              Access
            </Tabs.Item>
          </Tabs>
        </div>
      )}
    </Grid>
  );
}
